import type { AppPolicy, Claims } from '@helios/authz';

/**
 * Roles provisioned for web-admin tenants (from the RBAC CSV).
 * Used only to validate that a signed-in user carries a recognizable role
 * claim; actual authorization is permissions-derived (Firestore role docs),
 * not this list.
 */
export const KNOWN_ROLES = ['admin', 'doctor', 'front_desk', 'pharmacist'] as const;

/**
 * Legacy app policy, retained for the No-Access screen's debug display.
 * PRD.268 replaced role-list entry gating with permissions-derived entry
 * (see hasValidRoleClaim + PermissionProvider.hasAccess in App.tsx).
 */
export const APP_POLICY: AppPolicy = {
  appId: 'web-admin',
  entryAnyOfRoles: [...KNOWN_ROLES],
} as const;

/**
 * True if the user has the minimum identity claims to *attempt* entry: an
 * active org and at least one role in it. The final entry decision is made
 * after permissions load (must grant at least one accessible module).
 */
export function hasValidRoleClaim(claims: Claims | null | undefined): boolean {
  if (!claims) return false;
  const { orgs, activeOrg } = claims;
  if (!activeOrg || !orgs) return false;
  const roles = orgs[activeOrg]?.roles;
  return Array.isArray(roles) && roles.length > 0;
}
